import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import { schedulingApi } from '../../services/api';

// Result shape of GET /api/scheduling/patients/search (internal contacts DB).
export interface SearchPatient {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  patientId?: string;
  spruceContactId?: string;
  internalId?: string;
  dateOfBirth?: string;
  gender?: string;
}

interface PatientLinkPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (patient: SearchPatient) => void | Promise<void>;
  busy?: boolean;
  error?: string | null;
}

// Search-and-pick dialog used to link (or relink) a triage task to a patient.
// Server-side search; options without any patient identifier are shown but
// disabled since a task can't be linked to them.
const PatientLinkPicker: React.FC<PatientLinkPickerProps> = ({ open, onClose, onSelect, busy = false, error = null }) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SearchPatient[]>([]);
  const [searching, setSearching] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    if (!open) {
      setInputValue('');
      setOptions([]);
      return;
    }
  }, [open]);

  useEffect(() => {
    const q = inputValue.trim();
    if (q.length < 2) {
      setOptions([]);
      return;
    }
    const seq = ++requestSeq.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await schedulingApi.searchPatients(q);
        if (seq !== requestSeq.current) return; // stale response
        const results = response.data?.data || response.data || [];
        setOptions(Array.isArray(results) ? results : []);
      } catch {
        if (seq === requestSeq.current) setOptions([]);
      } finally {
        if (seq === requestSeq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Must match the backend requirement: PATCH /tasks/:id/patient-link needs
  // an internalId (LMD) or a real Spruce contact id. The search result's
  // patientId is an internal UUID and doesn't count.
  const isLinkable = (p: SearchPatient) => Boolean(p.internalId || p.spruceContactId);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link patient</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search by name or phone, then confirm it's the right person before linking.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        <Autocomplete<SearchPatient>
          autoFocus
          options={options}
          loading={searching}
          filterOptions={(x) => x}
          getOptionLabel={(o) => o.name || ''}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          getOptionDisabled={(o) => !isLinkable(o)}
          inputValue={inputValue}
          onInputChange={(_, value) => setInputValue(value)}
          onChange={(_, value) => { if (value) onSelect(value); }}
          noOptionsText={inputValue.trim().length < 2 ? 'Type at least 2 characters' : 'No matching patients'}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[option.phone, option.dateOfBirth && `DOB ${option.dateOfBirth}`].filter(Boolean).join(' · ') || '—'}
                  {!isLinkable(option) && ' · no patient ID'}
                </Typography>
              </Box>
              {option.internalId && (
                <Chip label={option.internalId} size="small" variant="outlined" color="primary" />
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Patient name or phone"
              autoFocus
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {(searching || busy) && <CircularProgress size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientLinkPicker;
